class StickyBuyButton {
  constructor() {
    this.stickyBar = document.querySelector('.sticky-buy-button-bar');
    this.originalButton = document.querySelector('#ProductSubmitButton-' + this.getSectionId());
    this.originalForm = this.originalButton?.closest('form');
    this.stickyForm = this.stickyBar?.querySelector('form');
    this.stickyButton = this.stickyBar?.querySelector('.sticky-buy-button-submit');
    this.stickyVariantInput = this.stickyBar?.querySelector('.product-variant-id');
    
    if (!this.stickyBar || !this.originalButton) return;

    this.init();
  }

  getSectionId() {
    const productInfo = document.querySelector('product-info');
    return productInfo?.dataset?.section || '';
  }

  init() {
    // Check if original button is visible on load
    this.checkButtonVisibility();
    
    // Listen for scroll events
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.checkButtonVisibility();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Listen for resize events
    window.addEventListener('resize', () => {
      this.checkButtonVisibility();
    }, { passive: true });

    // Sync variant selection
    this.syncVariantSelection();

    // Handle sticky form submission
    if (this.stickyForm) {
      this.stickyForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleStickyFormSubmit();
      });
    }

    // Update sticky button state when original button state changes
    this.observeOriginalButton();

    // Listen for variant changes to update price
    this.listenForVariantChanges();
  }

  checkButtonVisibility() {
    if (!this.originalButton) return;

    const rect = this.originalButton.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    const isAboveViewport = rect.bottom < 0;

    // Show sticky bar if original button is above viewport or not visible
    if (isAboveViewport || (!isVisible && rect.top < window.innerHeight)) {
      this.showStickyBar();
    } else {
      this.hideStickyBar();
    }
  }

  showStickyBar() {
    if (this.stickyBar) {
      this.stickyBar.style.display = 'block';
      // Use requestAnimationFrame to ensure display is set before adding class
      requestAnimationFrame(() => {
        this.stickyBar.classList.add('show');
      });
    }
  }

  hideStickyBar() {
    if (this.stickyBar) {
      this.stickyBar.classList.remove('show');
      // Hide after transition completes
      setTimeout(() => {
        if (!this.stickyBar.classList.contains('show')) {
          this.stickyBar.style.display = 'none';
        }
      }, 300);
    }
  }

  syncVariantSelection() {
    if (!this.originalForm || !this.stickyVariantInput) return;

    // Watch for variant changes in the original form
    const originalVariantInput = this.originalForm.querySelector('.product-variant-id');
    if (!originalVariantInput) return;

    // Initial sync
    this.updateStickyVariant(originalVariantInput.value);

    // Watch for changes
    const observer = new MutationObserver(() => {
      this.updateStickyVariant(originalVariantInput.value);
      this.updateStickyButtonState();
    });

    observer.observe(originalVariantInput, {
      attributes: true,
      attributeFilter: ['value', 'disabled']
    });

    // Also listen for input events (in case variant picker updates it)
    originalVariantInput.addEventListener('change', () => {
      this.updateStickyVariant(originalVariantInput.value);
      this.updateStickyButtonState();
    });

    // Watch for variant picker changes
    const variantPickers = document.querySelectorAll('[name="id"]');
    variantPickers.forEach(picker => {
      if (picker !== originalVariantInput && picker !== this.stickyVariantInput) {
        picker.addEventListener('change', () => {
          setTimeout(() => {
            this.updateStickyVariant(originalVariantInput.value);
            this.updateStickyButtonState();
          }, 100);
        });
      }
    });
  }

  updateStickyVariant(variantId) {
    if (this.stickyVariantInput) {
      this.stickyVariantInput.value = variantId;
    }
  }

  updateStickyButtonState() {
    if (!this.originalButton || !this.stickyButton) return;

    // Sync disabled state
    const isDisabled = this.originalButton.disabled || 
                      this.originalButton.getAttribute('aria-disabled') === 'true';
    
    this.stickyButton.disabled = isDisabled;

    // Sync button text
    const originalText = this.originalButton.querySelector('.button__text');
    const stickyText = this.stickyButton.querySelector('.button__text');
    
    if (originalText && stickyText) {
      stickyText.textContent = originalText.textContent.trim();
    }

    // Sync button classes
    if (isDisabled) {
      this.stickyButton.classList.add('button--disabled');
    } else {
      this.stickyButton.classList.remove('button--disabled');
    }
  }

  observeOriginalButton() {
    if (!this.originalButton) return;

    const observer = new MutationObserver(() => {
      this.updateStickyButtonState();
    });

    observer.observe(this.originalButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      subtree: true,
      childList: true
    });

    // Initial state update
    this.updateStickyButtonState();
  }

  listenForVariantChanges() {
    // Listen for variant change events to update price
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data && event.data.html) {
          this.updateStickyPrice(event.data.html);
        }
      });
    }

    // Also watch for price element changes
    const priceElement = document.querySelector(`#price-${this.getSectionId()}`);
    if (priceElement) {
      const observer = new MutationObserver(() => {
        this.updateStickyPriceFromDOM();
      });
      observer.observe(priceElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  updateStickyPrice(html) {
    if (!html) return;
    
    const sectionId = this.getSectionId();
    const sourcePrice = html.getElementById(`price-${sectionId}`);
    const stickyPriceElement = this.stickyBar?.querySelector('.sticky-buy-button-price');
    
    if (sourcePrice && stickyPriceElement) {
      stickyPriceElement.innerHTML = sourcePrice.innerHTML;
    }
  }

  updateStickyPriceFromDOM() {
    const sectionId = this.getSectionId();
    const sourcePrice = document.querySelector(`#price-${sectionId}`);
    const stickyPriceElement = this.stickyBar?.querySelector('.sticky-buy-button-price');
    
    if (sourcePrice && stickyPriceElement) {
      stickyPriceElement.innerHTML = sourcePrice.innerHTML;
    }
  }

  handleStickyFormSubmit() {
    if (!this.originalForm || !this.stickyForm) return;

    // Update original form with sticky form data
    const stickyFormData = new FormData(this.stickyForm);
    const originalVariantInput = this.originalForm.querySelector('.product-variant-id');
    
    if (originalVariantInput) {
      originalVariantInput.value = stickyFormData.get('id');
    }

    // Find the product-form custom element
    const productFormElement = this.originalForm.closest('product-form');
    
    if (productFormElement) {
      // Trigger the original button click which will handle everything
      if (this.originalButton) {
        // Update button state
        this.stickyButton.classList.add('loading');
        const stickySpinner = this.stickyButton.querySelector('.loading__spinner');
        if (stickySpinner) {
          stickySpinner.classList.remove('hidden');
        }
        this.stickyButton.disabled = true;

        // Click the original button to trigger the product-form handler
        this.originalButton.click();
        
        // Sync loading state
        const checkLoading = setInterval(() => {
          const isOriginalLoading = this.originalButton.classList.contains('loading');
          if (!isOriginalLoading) {
            this.stickyButton.classList.remove('loading');
            if (stickySpinner) {
              stickySpinner.classList.add('hidden');
            }
            this.updateStickyButtonState();
            clearInterval(checkLoading);
          }
        }, 100);
      }
    } else {
      // Fallback: submit the form directly
      this.originalForm.requestSubmit();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyBuyButton();
  });
} else {
  new StickyBuyButton();
}

